import * as React from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { motion } from "motion/react";
import styled from "styled-components";
import { DisablePortalToBodyContext } from "@sproutsocial/seeds-react-portal";
import {
  BODY_PADDING,
  DEFAULT_MODAL_WIDTH,
  RAIL_CENTERING_OFFSET,
  RAIL_EXTRA_SPACE,
} from "../../shared/constants";
import {
  COMMON,
  FLEXBOX,
  BORDER,
  LAYOUT,
  type TypeSystemCommonProps,
  type TypeSystemBorderProps,
  type TypeSystemLayoutProps,
  type TypeSystemFlexboxProps,
} from "@sproutsocial/seeds-react-system-props";
import { getContentVariants } from "../MotionConfig";

interface StyledContentProps
  extends TypeSystemCommonProps,
    TypeSystemFlexboxProps,
    TypeSystemBorderProps,
    TypeSystemLayoutProps {
  isDragging?: boolean;
  draggable?: boolean;
}

// Styled motion.div wrapper that handles positioning
const StyledMotionWrapper = styled(motion.div)<{
  $zIndex?: number;
}>`
  position: fixed;
  top: 50%;
  /* Optically centered, not geometrically: the rail is positioned against this
     wrapper and hangs off the card's right edge, so pull the whole group left by
     half the rail's outset to even out the gutters. See RAIL_CENTERING_OFFSET. */
  left: calc(50% - ${RAIL_CENTERING_OFFSET}px);
  z-index: ${(props) => (props.$zIndex ? props.$zIndex + 1 : 7)};
`;

export const StyledContent = styled.div.withConfig({
  shouldForwardProp: (prop) => !["isDragging", "draggable"].includes(prop),
})<StyledContentProps>`
  display: flex;
  flex-direction: column;
  border-radius: ${(props) => props.theme.radii[800]};
  box-shadow: ${(props) => props.theme.shadows.high};
  filter: blur(0);
  background-color: ${(props) => props.theme.colors.container.background.base};
  color: ${(props) => props.theme.colors.text.body};
  outline: none;
  width: ${DEFAULT_MODAL_WIDTH};
  /* Account for rail space on the side; shrinks to fit small viewports */
  max-width: calc(100vw - ${BODY_PADDING} - ${RAIL_EXTRA_SPACE}px);
  max-height: calc(100vh - ${BODY_PADDING});

  @media screen and (-ms-high-contrast: active), (-ms-high-contrast: none) {
    height: calc(100vh - ${BODY_PADDING});
  }

  ${COMMON}
  ${FLEXBOX}
  ${BORDER}
  ${LAYOUT}
`;

StyledContent.displayName = "ModalContent";

// Context to share drag state between modal content and header
interface DragContextValue {
  position: { x: number; y: number };
  isDragging: boolean;
  onHeaderMouseDown: (e: React.MouseEvent) => void;
  contentRef: React.RefObject<HTMLDivElement>;
  draggable: boolean;
}

export const DragContext = React.createContext<DragContextValue | null>(null);

export const useDragContext = () => {
  const context = React.useContext(DragContext);
  return context;
};

interface ModalContentProps {
  children: React.ReactNode;
  label?: string;
  dataAttributes: Record<string, string>;
  draggable?: boolean;
  zIndex?: number;
  rest: any;
  dialogContentProps?: Pick<
    React.ComponentPropsWithoutRef<typeof Dialog.Content>,
    | "onOpenAutoFocus"
    | "onCloseAutoFocus"
    | "onEscapeKeyDown"
    | "onPointerDownOutside"
    | "onFocusOutside"
    | "onInteractOutside"
  >;
}

/**
 * Static modal content component for non-draggable modals.
 * This is a lightweight version that doesn't include any drag logic.
 */
export const StaticModalContent: React.FC<ModalContentProps> = ({
  children,
  label,
  dataAttributes,
  zIndex,
  rest,
  dialogContentProps,
}) => {
  const contentVariants = getContentVariants(false);

  return (
    <DragContext.Provider value={null}>
      <Dialog.Content asChild aria-label={label} {...dialogContentProps}>
        <StyledMotionWrapper
          $zIndex={zIndex}
          variants={contentVariants}
          initial="initial"
          animate="animate"
          exit="exit"
        >
          <StyledContent
            data-slot="modal-content"
            draggable={false}
            {...dataAttributes}
            {...rest}
          >
            <DisablePortalToBodyContext.Provider value={true}>
              {children}
            </DisablePortalToBodyContext.Provider>
          </StyledContent>
        </StyledMotionWrapper>
      </Dialog.Content>
    </DragContext.Provider>
  );
};

/**
 * Draggable modal content component with full drag-and-drop functionality.
 * Only rendered when draggable={true} to avoid unnecessary overhead.
 */
export const DraggableModalContent: React.FC<ModalContentProps> = ({
  children,
  label,
  dataAttributes,
  zIndex,
  rest,
  dialogContentProps,
}) => {
  const [position, setPosition] = React.useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = React.useState(false);
  const contentRef = React.useRef<HTMLDivElement>(null);

  const handleHeaderMouseDown = React.useCallback((e: React.MouseEvent) => {
    // Only allow dragging from header (not interactive elements)
    const target = e.target as HTMLElement;
    if (
      target.tagName === "BUTTON" ||
      target.tagName === "INPUT" ||
      target.closest("button")
    ) {
      return;
    }

    e.preventDefault();
    setIsDragging(true);

    const rect = contentRef.current?.getBoundingClientRect();
    if (!rect) return;

    // Calculate offset from mouse to current modal position
    const offsetX = e.clientX - rect.left;
    const offsetY = e.clientY - rect.top;

    const handleMouseMove = (e: MouseEvent) => {
      e.preventDefault();

      // Calculate new position based on mouse position minus offset
      const newX = e.clientX - offsetX;
      const newY = e.clientY - offsetY;

      // Constrain to viewport bounds (keeping modal AND rail fully visible)
      const modalWidth = rect.width;
      const modalHeight = rect.height;

      const minX = 0;
      // Rail is always on the right side; reserve space so it can't be dragged off-screen
      const maxX = window.innerWidth - modalWidth - RAIL_EXTRA_SPACE;
      const minY = 0;
      const maxY = window.innerHeight - modalHeight;

      const constrainedX = Math.max(minX, Math.min(maxX, newX));
      const constrainedY = Math.max(minY, Math.min(maxY, newY));

      // Convert to offset from center for our transform. The wrapper renders at
      // left: calc(50% - RAIL_CENTERING_OFFSET), so fold that into the baseline or
      // the modal jumps left on the first drag.
      const centerX =
        window.innerWidth / 2 - modalWidth / 2 - RAIL_CENTERING_OFFSET;
      const centerY = window.innerHeight / 2 - modalHeight / 2;

      setPosition({
        x: constrainedX - centerX,
        y: constrainedY - centerY,
      });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  }, []);

  const dragContextValue = React.useMemo<DragContextValue>(
    () => ({
      position,
      isDragging,
      onHeaderMouseDown: handleHeaderMouseDown,
      contentRef,
      draggable: true,
    }),
    [position, isDragging, handleHeaderMouseDown]
  );

  // Prevent modal from closing on outside interaction when draggable
  const handleInteractOutside = React.useCallback((e: Event) => {
    e.preventDefault();
  }, []);

  // Get appropriate animation variants based on context
  const contentVariants = getContentVariants(true);

  return (
    <DragContext.Provider value={dragContextValue}>
      <Dialog.Content
        asChild
        aria-label={label}
        {...dialogContentProps}
        onInteractOutside={handleInteractOutside}
      >
        <StyledMotionWrapper
          $zIndex={zIndex}
          variants={contentVariants}
          initial="initial"
          animate="animate"
          exit="exit"
          style={{
            // Apply drag offset transforms
            // For draggable modals, variants only handle opacity, so we apply transforms here
            // Combined with top: 50%, left: 50%, these create the centering + drag offset
            x: `calc(-50% + ${position.x}px)`,
            y: `calc(-50% + ${position.y}px)`,
          }}
        >
          <StyledContent
            data-slot="modal-content"
            ref={contentRef}
            draggable={true}
            isDragging={isDragging}
            {...dataAttributes}
            {...rest}
          >
            {children}
          </StyledContent>
        </StyledMotionWrapper>
      </Dialog.Content>
    </DragContext.Provider>
  );
};
